import React, { useState } from 'react';
import {
  Building2,
  ArrowRight,
  Zap,
  DollarSign,
  CheckCircle2,
  Shield,
  ChevronDown,
  ChevronUp,
  Star,
  Check,
  X,
  Crown,
  Mail,
} from 'lucide-react';
import FeatureFlipCards from '../../components/landing/FeatureFlipCards';
import FeatureDeepDive from '../../components/landing/FeatureDeepDive';
import HeroMockup from '../../components/landing/HeroMockup';
import BeforeAfterComparison from '../../components/landing/BeforeAfterComparison';

// ── FAQ items ────────────────────────────────────────────────────
const faqItems = [
  {
    q: 'Is this really free to start?',
    a: "Yes. The Starter plan is free forever — you get deal analysis, expense tracking, and up to 2 active projects with no credit card required. Upgrade only when you're ready to scale.",
  },
  {
    q: "I'm a solo flipper. Is this just for teams?",
    a: 'Not at all. FlipperPro works great for solo flippers managing 1-2 projects and for teams running 20+. The team features are there when you need them.',
  },
  {
    q: 'Can I track expenses and sync with QuickBooks?',
    a: 'Yes. You can manually categorize expenses, scan receipts, and connect your QuickBooks account to sync transactions automatically.',
  },
  {
    q: 'How is this different from a spreadsheet?',
    a: "Spreadsheets don't give you deal calculators, team collaboration, portfolio analytics, photo logs, scheduler syncing, or a visual project dashboard. FlipperPro replaces 5+ tools with one.",
  },
  {
    q: 'Is my data secure?',
    a: "Your data is hosted on Google Cloud (Firebase) with enterprise-grade encryption, role-based access controls, and isolated team workspaces. Each company's data is completely separated.",
  },
];

// ── Pricing tiers ────────────────────────────────────────────────
const pricingTiers = [
  {
    name: 'Starter',
    monthlyPrice: 0,
    annualPrice: 0,
    description: 'Perfect for solo flippers getting started with their first few deals.',
    cta: 'Start Free',
    highlighted: false,
    features: [
      { text: 'Up to 2 active projects', included: true },
      { text: 'Flip calculator & deal analysis', included: true },
      { text: 'Rehab estimator with templates', included: true },
      { text: 'Loan calculator', included: true },
      { text: 'Basic expense tracking (manual entry)', included: true },
      { text: 'Photo logs (50 per project)', included: true },
      { text: 'Portfolio tracking (table view)', included: true },
      { text: 'Basic dashboard', included: true },
      { text: 'Single user', included: true },
    ],
  },
  {
    name: 'Pro',
    monthlyPrice: 100,
    annualPrice: 80,
    description: 'Full access for growing flippers managing up to 10 active projects.',
    cta: 'Start Free Trial',
    highlighted: true,
    badge: 'Most Popular',
    features: [
      { text: 'Up to 10 active projects', included: true },
      { text: 'Everything in Starter, plus:', included: true },
      { text: 'Lead pipeline & deal acquisition', included: true },
      { text: 'Receipt scanning (AI-powered)', included: true },
      { text: 'Scheduler, tasks & Kanban board', included: true },
      { text: 'Risk monitoring & live analysis', included: true },
      { text: 'Portfolio analytics & reports', included: true },
      { text: 'CSV export', included: true },
      { text: 'QuickBooks integration', included: true },
      { text: 'Photo logs (200 per project)', included: true },
      { text: 'Sales tracking', included: true },
      { text: '5 GB storage', included: true },
      { text: 'Priority email support', included: true },
    ],
  },
  {
    name: 'Business',
    monthlyPrice: 200,
    annualPrice: 160,
    description: 'For teams and companies that need unlimited scale and collaboration.',
    cta: 'Start Free Trial',
    highlighted: false,
    features: [
      { text: 'Unlimited active projects', included: true },
      { text: 'Everything in Pro, plus:', included: true },
      { text: 'Unlimited team members & all roles', included: true },
      { text: 'Unlimited photo logs & storage', included: true },
      { text: 'Full deal pipeline (CFO/CEO review)', included: true },
      { text: 'Capital tracking dashboard', included: true },
      { text: 'Approval workflows', included: true },
      { text: 'All 7 reports + Investment Deck', included: true },
      { text: 'Department teams', included: true },
      { text: 'Automated weekly reviews', included: true },
      { text: 'Dedicated account manager', included: true },
    ],
  },
];

// ── FAQ Accordion Item ───────────────────────────────────────────
function FAQItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left group"
      >
        <span className="text-[15px] font-semibold text-white group-hover:text-blue-300 transition-colors pr-4">
          {item.q}
        </span>
        {open ? (
          <ChevronUp size={18} className="text-blue-400 flex-shrink-0" />
        ) : (
          <ChevronDown size={18} className="text-slate-500 flex-shrink-0" />
        )}
      </button>
      <div
        className="overflow-hidden transition-all duration-300"
        style={{ maxHeight: open ? 200 : 0, opacity: open ? 1 : 0 }}
      >
        <p className="text-sm text-blue-200/70 pb-5 leading-relaxed">{item.a}</p>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  MAIN LANDING PAGE
// ════════════════════════════════════════════════════════════════
export default function LandingPage({ setAuthPage }) {
  const [isAnnual, setIsAnnual] = useState(true);
  const [selectedTier, setSelectedTier] = useState('Pro');

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950"
      style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        .hero-gradient { background: radial-gradient(ellipse 80% 60% at 50% 20%, rgba(99,102,241,0.12) 0%, transparent 60%); }
        .card-hover { transition: all 0.3s cubic-bezier(0.4,0,0.2,1); }
        .card-hover:hover { transform: translateY(-6px); box-shadow: 0 24px 48px rgba(0,0,0,0.35); border-color: rgba(139,92,246,0.3); }
        .glow { box-shadow: 0 0 80px rgba(99,102,241,0.2), 0 0 160px rgba(99,102,241,0.08); }
        .cta-pulse { animation: pulse-glow 2.5s ease-in-out infinite; }
        @keyframes pulse-glow { 0%,100%{ box-shadow: 0 0 20px rgba(99,102,241,0.3); } 50%{ box-shadow: 0 0 40px rgba(99,102,241,0.5); } }
        .fade-up { animation: fadeUp 0.6s ease-out both; }
        @keyframes fadeUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
        .flip-card { cursor: pointer; }
        .flip-card-inner { min-height: 240px; }
        .flip-card-front, .flip-card-back { min-height: 240px; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* ── Navigation ──────────────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/70 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
              <Building2 size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white tracking-tight">FlipperPro</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() =>
                document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })
              }
              className="px-4 py-2.5 text-sm font-medium text-slate-300 hover:text-white transition-colors hidden md:inline-block"
            >
              Features
            </button>
            <button
              onClick={() =>
                document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })
              }
              className="px-4 py-2.5 text-sm font-medium text-slate-300 hover:text-white transition-colors hidden md:inline-block"
            >
              How It Works
            </button>
            <button
              onClick={() =>
                document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })
              }
              className="px-4 py-2.5 text-sm font-medium text-slate-300 hover:text-white transition-colors hidden sm:inline-block"
            >
              Pricing
            </button>
            <button
              onClick={() => setAuthPage('signin')}
              className="px-5 py-2.5 text-sm font-medium text-slate-300 hover:text-white transition-colors"
            >
              Sign In
            </button>
            <button
              onClick={() => setAuthPage('signup')}
              className="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-violet-600 rounded-xl hover:from-blue-600 hover:to-violet-700 transition-all shadow-lg shadow-violet-500/20"
            >
              Start Free Trial
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero Section ───────────────────────────────── */}
      <section className="hero-gradient pt-32 pb-12 md:pt-36 md:pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto fade-up">
            {/* Trust badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-500/10 border border-violet-500/20 rounded-full mb-7">
              <Zap size={14} className="text-violet-400" />
              <span className="text-sm text-violet-300 font-medium">
                Built for flippers, by flippers
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-5xl md:text-7xl font-black text-white leading-[1.08] mb-6 tracking-tight">
              Buy. Renovate.{' '}
              <span className="bg-gradient-to-r from-blue-400 via-violet-400 to-purple-400 bg-clip-text text-transparent">
                Profit.
              </span>
            </h1>

            {/* Subheadline — outcome-focused */}
            <p className="text-lg md:text-xl text-slate-400 mb-10 max-w-2xl mx-auto leading-relaxed">
              Analyze deals, manage rehabs, and track every dollar — all in one platform built for
              flippers.
            </p>

            {/* Hero CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4">
              <button
                onClick={() => setAuthPage('signup')}
                className="cta-pulse px-9 py-4 text-base font-bold text-white bg-gradient-to-r from-blue-500 to-violet-600 rounded-xl hover:from-blue-600 hover:to-violet-700 transition-all shadow-xl shadow-violet-500/25 flex items-center gap-2.5"
              >
                Start Your Free Trial <ArrowRight size={18} />
              </button>
            </div>
            <p className="text-xs text-slate-500 font-medium flex items-center justify-center gap-1.5">
              <Shield size={12} className="text-emerald-500" /> No credit card required
              &nbsp;·&nbsp; Free forever for small teams
            </p>

            {/* Inline social proof stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10 max-w-3xl mx-auto">
              {[
                { value: '500+', label: 'Active Flippers' },
                { value: '2,400+', label: 'Projects Managed' },
                { value: '$50M+', label: 'In Deals Tracked' },
                { value: '98%', label: 'Satisfaction Rate' },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <p className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
                    {stat.value}
                  </p>
                  <p className="text-[11px] text-blue-300/70 mt-1 font-medium">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── App Preview (cycling mockup) ──────────── */}
          <HeroMockup />
        </div>
      </section>

      {/* ── Problem + Before/After (consolidated) ────── */}
      <BeforeAfterComparison />

      {/* ── Features Section (Flip Cards) ────────────── */}
      <FeatureFlipCards />

      {/* ── Feature Deep Dive (Tabbed Showcase) ──────── */}
      <FeatureDeepDive onSignUp={() => setAuthPage('signup')} />

      {/* ── How It Works ────────────────────────────────── */}
      <section className="py-14 px-6 bg-slate-900/40" id="how-it-works">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">
              Up and Running in Minutes
            </h2>
            <p className="text-base text-slate-400 max-w-lg mx-auto">
              No onboarding calls. No setup fees. Just sign up and start.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                step: '01',
                title: 'Create Your Account',
                desc: 'Sign up free in 30 seconds. No credit card, no commitments.',
                icon: Shield,
              },
              {
                step: '02',
                title: 'Add Your First Flip',
                desc: 'Enter a property, run the deal calculator, and set up your project workspace.',
                icon: Building2,
              },
              {
                step: '03',
                title: 'Track Everything',
                desc: 'Manage tasks, expenses, photos, and team — all from one dashboard.',
                icon: CheckCircle2,
              },
            ].map((item, i) => (
              <div key={i} className="relative text-center">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500/20 to-violet-500/20 border border-white/[0.08] flex items-center justify-center mx-auto mb-4">
                  <item.icon size={24} className="text-violet-400" />
                </div>
                <span className="text-[11px] font-bold text-violet-500 tracking-widest uppercase">
                  Step {item.step}
                </span>
                <h3 className="text-lg font-bold text-white mt-2 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing Section ──────────────────────────────── */}
      <section className="py-14 px-6" id="pricing">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-500/10 border border-violet-500/20 rounded-full mb-5">
              <DollarSign size={14} className="text-violet-400" />
              <span className="text-sm text-violet-300 font-medium">
                Simple, Transparent Pricing
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">
              Plans That Scale With Your Business
            </h2>
            <p className="text-base text-slate-400 max-w-xl mx-auto mb-8">
              Start free and upgrade as you grow. Every plan includes a 14-day free trial — no
              credit card required.
            </p>

            {/* Billing toggle */}
            <div className="inline-flex items-center gap-3 bg-white/[0.04] border border-white/[0.08] rounded-full px-1.5 py-1.5">
              <button
                onClick={() => setIsAnnual(false)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                  !isAnnual
                    ? 'bg-gradient-to-r from-blue-500 to-violet-600 text-white shadow-lg shadow-violet-500/20'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setIsAnnual(true)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-2 ${
                  isAnnual
                    ? 'bg-gradient-to-r from-blue-500 to-violet-600 text-white shadow-lg shadow-violet-500/20'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Annual
                <span
                  className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                    isAnnual ? 'bg-white/20 text-white' : 'bg-emerald-500/15 text-emerald-400'
                  }`}
                >
                  Save 20%
                </span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto items-start">
            {pricingTiers.map((tier, i) => {
              const price = isAnnual ? tier.annualPrice : tier.monthlyPrice;
              const isSelected = selectedTier === tier.name;
              return (
                <div
                  key={i}
                  onClick={() => setSelectedTier(tier.name)}
                  className={`relative rounded-2xl p-6 flex flex-col border transition-all cursor-pointer ${
                    isSelected
                      ? 'bg-gradient-to-b from-violet-500/[0.08] to-blue-500/[0.04] border-violet-500/30 shadow-xl shadow-violet-500/10 scale-[1.03] md:scale-105 ring-2 ring-violet-500/40'
                      : 'bg-white/[0.03] border-white/[0.07] hover:border-white/[0.15]'
                  }`}
                >
                  {tier.badge && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                      <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-gradient-to-r from-blue-500 to-violet-600 rounded-full text-xs font-bold text-white shadow-lg shadow-violet-500/25">
                        <Crown size={12} /> {tier.badge}
                      </span>
                    </div>
                  )}

                  <div className={tier.badge ? 'mt-2' : ''}>
                    <h3 className="text-lg font-bold text-white mb-1">{tier.name}</h3>
                    <p className="text-sm text-slate-400 mb-5 leading-relaxed">
                      {tier.description}
                    </p>

                    <div className="flex items-baseline gap-1 mb-1">
                      <span className="text-4xl font-extrabold text-white">${price}</span>
                      <span className="text-sm text-slate-500 font-medium">/mo</span>
                    </div>
                    {isAnnual && tier.monthlyPrice > 0 ? (
                      <p className="text-xs text-emerald-400 font-medium mb-5">
                        ${tier.annualPrice * 12}/yr · Save $
                        {(tier.monthlyPrice - tier.annualPrice) * 12}/yr
                      </p>
                    ) : (
                      <p className="text-xs text-slate-600 mb-5">
                        {tier.monthlyPrice === 0
                          ? 'Free forever'
                          : `$${tier.monthlyPrice * 12}/yr billed monthly`}
                      </p>
                    )}

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setAuthPage('signup');
                      }}
                      className={`w-full py-3 rounded-xl text-sm font-bold transition-all mb-6 ${
                        isSelected
                          ? 'bg-gradient-to-r from-blue-500 to-violet-600 text-white hover:from-blue-600 hover:to-violet-700 shadow-lg shadow-violet-500/20'
                          : 'bg-white/[0.07] text-white hover:bg-white/[0.12] border border-white/[0.1]'
                      }`}
                    >
                      {tier.cta}
                    </button>

                    <div className="space-y-3">
                      {tier.features.map((feature, j) => (
                        <div key={j} className="flex items-start gap-2.5">
                          {feature.included ? (
                            <Check size={16} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                          ) : (
                            <X size={16} className="text-slate-600 mt-0.5 flex-shrink-0" />
                          )}
                          <span
                            className={`text-sm leading-snug ${feature.included ? 'text-slate-300' : 'text-slate-600'}`}
                          >
                            {feature.text}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="text-center text-xs text-slate-500 mt-8 flex items-center justify-center gap-1.5">
            <Shield size={12} className="text-emerald-500" /> All plans include SSL encryption
            &nbsp;·&nbsp; Cancel anytime &nbsp;·&nbsp; No hidden fees
          </p>
        </div>
      </section>

      {/* ── Testimonials / Social Proof ─────────────────── */}
      <section className="py-14 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">
              Trusted by Flippers Nationwide
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                quote:
                  'I used to track everything in Google Sheets. FlipperPro saved me at least 10 hours a week and I caught a $15K budget overrun I would have missed.',
                name: 'Marcus T.',
                initials: 'MT',
                avatarColor: 'from-blue-500 to-cyan-500',
                role: 'Full-time flipper · Dallas, TX',
                flips: '24 flips completed',
              },
              {
                quote:
                  "The deal calculator alone is worth it. I passed on two properties that looked good on paper but would have lost me money. Data doesn't lie.",
                name: 'Rachel K.',
                initials: 'RK',
                avatarColor: 'from-violet-500 to-purple-500',
                role: 'Real estate investor · Phoenix, AZ',
                flips: '11 flips completed',
              },
              {
                quote:
                  'My team of 6 was drowning in text chains and missed deadlines. Now everyone sees the same dashboard. Game changer for accountability.',
                name: 'David L.',
                initials: 'DL',
                avatarColor: 'from-emerald-500 to-teal-500',
                role: 'Flipping company owner · Atlanta, GA',
                flips: '40+ flips completed',
              },
            ].map((t, i) => (
              <div
                key={i}
                className={`bg-white/[0.04] border border-white/[0.08] rounded-2xl p-6 flex flex-col ${i === 2 ? 'hidden md:flex' : ''}`}
              >
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} size={14} className="text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-sm text-slate-300 leading-relaxed flex-1 mb-4">"{t.quote}"</p>
                <div className="border-t border-white/[0.06] pt-4 flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-full bg-gradient-to-br ${t.avatarColor} flex items-center justify-center flex-shrink-0`}
                  >
                    <span className="text-xs font-bold text-white">{t.initials}</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{t.name}</p>
                    <p className="text-xs text-slate-500">{t.role}</p>
                    <p className="text-[11px] text-violet-400 font-medium mt-0.5">{t.flips}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ Section ─────────────────────────────────── */}
      <section className="py-14 px-6 bg-slate-900/40">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-8 text-center tracking-tight">
            Frequently Asked Questions
          </h2>
          <div className="bg-white/[0.03] border border-white/[0.07] rounded-2xl px-6">
            {faqItems.map((item, i) => (
              <FAQItem key={i} item={item} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ──────────────────────────────────── */}
      <section className="py-14 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-violet-600/15 to-blue-600/15 rounded-3xl p-8 md:p-12 border border-violet-500/[0.15] relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-violet-500/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">
                Your Next Profitable Flip Starts Here
              </h2>
              <p className="text-base text-slate-400 mb-6 max-w-lg mx-auto">
                Join hundreds of house flippers who switched from spreadsheets to FlipperPro — and
                never looked back.
              </p>
              <button
                onClick={() => setAuthPage('signup')}
                className="cta-pulse px-10 py-4 text-base font-bold text-white bg-gradient-to-r from-blue-500 to-violet-600 rounded-xl hover:from-blue-600 hover:to-violet-700 transition-all shadow-xl shadow-violet-500/25 mb-4"
              >
                Start Free Trial — No Credit Card Required
              </button>
              <div className="flex items-center justify-center gap-6 text-xs text-slate-500">
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 size={13} className="text-emerald-500" /> Free forever plan
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 size={13} className="text-emerald-500" /> Setup in 30 seconds
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 size={13} className="text-emerald-500" /> Cancel anytime
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────── */}
      <footer className="pt-12 pb-8 px-6 border-t border-white/[0.06] bg-slate-950/80">
        <div className="max-w-7xl mx-auto">
          {/* Footer columns */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-10 mb-10">
            {/* Brand column */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
                  <Building2 size={18} className="text-white" />
                </div>
                <div>
                  <p className="text-lg font-bold text-white leading-tight">FlipperPro</p>
                </div>
              </div>
              <p className="text-sm text-slate-500 leading-relaxed max-w-xs mb-3">
                The all-in-one platform for house flippers to analyze deals, manage rehabs, and
                maximize profits.
              </p>
              <a
                href="mailto:support@flipperpro.com"
                className="text-sm text-violet-400 hover:text-violet-300 transition-colors flex items-center gap-1.5"
              >
                <Mail size={13} /> support@flipperpro.com
              </a>
            </div>

            {/* Product column */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2.5">
                <li>
                  <button
                    onClick={() =>
                      document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })
                    }
                    className="text-sm text-slate-500 hover:text-white transition-colors"
                  >
                    Features
                  </button>
                </li>
                <li>
                  <button
                    onClick={() =>
                      document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })
                    }
                    className="text-sm text-slate-500 hover:text-white transition-colors"
                  >
                    Pricing
                  </button>
                </li>
              </ul>
            </div>

            {/* Legal column */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2.5">
                <li>
                  <button
                    onClick={() => setAuthPage('privacy')}
                    className="text-sm text-slate-500 hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setAuthPage('terms')}
                    className="text-sm text-slate-500 hover:text-white transition-colors"
                  >
                    Terms of Service
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="pt-6 border-t border-white/[0.06] flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-slate-600">
              &copy; {new Date().getFullYear()} FlipperPro. All rights reserved.
            </p>
            <p className="text-xs text-slate-600">Built with ♡ for house flippers everywhere</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
